"use client"

import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"
import "dayjs/locale/he"
import { useTranslations } from "next-intl"
import { Dispatch, SetStateAction, useCallback, useEffect, useId, useMemo, useState } from "react"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { CalendarIcon, CheckboxIcon, CheckedCheckboxIcon, CircleXIcon, NewText } from "ui"
import { sharedColors } from "ui/theme/colors"
import { jobFilter } from "utils"
import * as yup from "yup"
import { FormInput as ApState } from "./UmbracoApartments/FiltersModal"

// Normalizes feature keys so form field names stay stable (quotes/apostrophes → safe keys)
const sanitizeFeatureKey = (key: string) => key.replace(/"/g, "").replace(/'/g, "&")
const buildFeatureKeyMap = (keys: string[]) =>
  keys.reduce((acc, key) => {
    acc[sanitizeFeatureKey(key)] = key
    return acc
  }, {} as Record<string, string>)
const sanitizeFeatureValues = (
  values: Record<string, boolean | undefined>,
  keyMap: Record<string, string>,
) => {
  const sanitized: Record<string, boolean | undefined> = {}
  Object.entries(keyMap).forEach(([sanitizedKey, originalKey]) => {
    sanitized[sanitizedKey] = values?.[originalKey] ?? values?.[sanitizedKey] ?? false
  })
  return sanitized
}
const desanitizeFeatureValues = (
  values: Record<string, boolean | undefined>,
  keyMap: Record<string, string>,
) => {
  const desanitized: Record<string, boolean | undefined> = {}
  Object.entries(values || {}).forEach(([sanitizedKey, value]) => {
    const originalKey = keyMap[sanitizedKey] || sanitizedKey
    desanitized[originalKey] = value
  })
  return desanitized
}

const StyledModal = styled.form`
  position: absolute;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  /* Fallback for browsers that don't support dvh */
  height: 85vh;
  max-height: 85vh;
  /* Use dvh (dynamic viewport height) - accounts for browser UI changes on mobile */
  height: 85dvh;
  max-height: 85dvh;
  background-color: white;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  ${({ theme }) => theme.breakpoints.up("md")} {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 660px;
    height: 584px;
    max-height: 584px;
  }
`
const ModalHeader = styled.div`
  background-color: ${(props) => props.theme.colors.propBlue};
  height: 68px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ theme }) => theme.breakpoints.up("md")} {
    height: 60px;
  }
`
const StyledButton = styled.div`
  margin: 0;
  padding: 0;
  width: 24px;
  height: 24px;
  cursor: pointer;
`

const ModalFooter = styled.div`
  height: 80px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  border-top: 2px solid #f7f7f7;
  align-items: center;
`

const ModalContent = styled.div`
  flex-grow: 1;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  gap: 32px;
`
const FirstPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`
const SecondPart = styled.div`
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 64px;
  border-bottom: 1px solid #f7f7f7;
  border-top: 1px solid #f7f7f7;
`

const Width = styled.div`
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 60%;
  }
`
const ThirdPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 60%;
  }
`
const DateStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const StyledNumberField = styled(TextField)`
  width: 100%;
  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`
const StyledRange = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Dash = styled.div`
  width: 16px;
  height: 1px;
  background-color: #6f6f6f;
  flex-shrink: 0;
  flex-grow: 0;
`

const BodyHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Numbers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`
const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  display: flex;

  gap: 8px;
`

const StyledToggleButton = styled(ToggleButton)`
  margin: 0;
  padding: 0;
  width: 100%;
  border-radius: 8px !important;
  padding: 8px 16px 8px 16px;
  font-size: 16px;
  line-height: 20px;
  height: 48px !important;
  color: black;
  border: 1px solid #6f6f6f !important;
  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 14px;
  }
  &.Mui-selected {
    background-color: white;
    border: 2px solid #000000 !important;
  }
`

const CheckArray = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 80px;
    row-gap: 8px;
    min-width: 160px;
  }
`
const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ error: boolean }>`
  ${(props) =>
    props.error
      ? `  rect {
    stroke: #d32f2f;
  }`
      : ""}

  &.Mui-focusVisible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const StyledDatePicker = styled(DatePicker)`
  .MuiInputBase-input {
    ::placeholder {
      color: #6f6f6f;
    }
  }
`

const StyledCheckFlex = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row-reverse;
  ${({ theme }) => theme.breakpoints.up("md")} {
    justify-content: unset;
    gap: 8px;
    flex-direction: row;
  }
`

const StyledResetButton = styled.button`
  font-size: 16px;
  outline: none;
  color: ${(props) => props.theme.colors.propBlue};
  border: none;
  background-color: transparent;
  cursor: pointer;
  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`
type GenericObject = { [key: string]: string }

export const buArray: GenericObject = {
  hasAccessible: "גישה לנכים",
  hasTenantRoom: "חדרי דיירים",
  hasGymboree: `ג'ימבורי`,
  hasGYM: "חדר כושר",
  hasElevator: "מעלית",
  hasCommercialFloor: "קומת מסחר",
  hasParkingLot: "חניון",
}

export const apArray: GenericObject = {
  // eslint-disable-next-line quotes
  hasMamad: 'ממ"ד',
  hasBalcony: "מרפסת",
  hasStorage: "מחסן",
  hasParking: "חניה",
  hasCentralAirConditioning: "מיזוג מרכזי",
  hasAirConditioning: "מיזוג עילי",
  hasSolarWaterHeater: "דוד שמש",
  hasBars: "סורגים",
}

export const buildingFeatures = {
  hasAccessible: "hasAccessible",
  hasTenantRoom: "hasTenantRoom",
  hasGymboree: `hasGymboree`,
  hasGYM: "hasGYM",
  hasElevator: "hasElevator",
  hasCommercialFloor: "hasCommercialFloor",
  hasParkingLot: "hasParkingLot",
} as const
export const apartmentFeatures = {
  hasMamad: "hasMamad",
  hasBalcony: "hasBalcony",
  hasStorage: "hasStorage",
  hasParking: "hasParking",
  hasCentralAirConditioning: "hasCentralAirConditioning",
  hasAirConditioning: "hasAirConditioning",
  hasSolarWaterHeater: "hasSolarWaterHeater",
  hasBars: "hasBars",
} as const

const floorArr: { value: number; label: string }[] = [{ value: 0, label: "קומת קרקע" }]

for (let i = 1; i <= 50; i++) {
  floorArr.push({ value: i, label: String(i) })
}

const roomArray = ["1", "2", "3", "4", "5", "6+"]

// Define the arrays
// const apartmentCharArray = [
//   "hasMamad",
//   "hasBalcony",
//   "hasStorage",
//   "hasParking",
//   "hasCentralAirConditioning",
//   "hasAirConditioning",
//   "hasSolarWaterHeater",
//   "hasBars",
// ]
// const buildingCharArray = [
//   "hasAccessible",
//   "hasTenantRoom",
//   "hasGymboree",
//   "hasGYM",
//   "hasElevator",
//   "hasCommercialFloor",
//   "hasParkingLot",
// ]

type Character = {
  [key: string]: yup.BooleanSchema<boolean | undefined, object>
}

// Function to create dynamic schema parts for apartment and building characteristics
const createCharSchema = (charArray: string[]) => {
  return charArray.reduce((acc, key) => {
    acc[key] = yup.boolean().optional()
    return acc
  }, {} as Character)
}

type Props = {
  open: boolean
  handleClose: () => void
  locale: string
  state: ApState
  setState: Dispatch<SetStateAction<ApState>>
  setFiltersCount: Dispatch<SetStateAction<number>>
  defaultState: ApState
  filtersCount: number
  handleReset: any
  projectPage?: boolean
  projectTitle?: string
  allApChar: string[]
  allBuChar: string[]
  availableBuildings?: string[]
  buildingFloorRanges?: Record<string, { min: number; max: number }>
}

function FiltersModal({
  handleClose,
  open,
  locale,
  setState,
  state,
  setFiltersCount,
  defaultState,
  handleReset,
  projectPage,
  projectTitle,
  allApChar,
  allBuChar,
  availableBuildings = [],
  buildingFloorRanges,
}: Props) {
  const dialogTitleId = useId()
  const shouldHideFilters = projectTitle?.trim() === "נווה איילון"

  const sanitizedApKeys = useMemo(
    () => allApChar.map((key) => sanitizeFeatureKey(key)),
    [allApChar],
  )
  const sanitizedBuKeys = useMemo(
    () => allBuChar.map((key) => sanitizeFeatureKey(key)),
    [allBuChar],
  )
  const apKeyMap = useMemo(() => buildFeatureKeyMap(allApChar), [allApChar])
  const buKeyMap = useMemo(() => buildFeatureKeyMap(allBuChar), [allBuChar])
  const sanitizeFormState = useCallback(
    (rawState: ApState): ApState => ({
      ...rawState,
      apartmentChar: sanitizeFeatureValues(rawState.apartmentChar || {}, apKeyMap),
      buildingChar: sanitizeFeatureValues(rawState.buildingChar || {}, buKeyMap),
    }),
    [apKeyMap, buKeyMap],
  )
  const formDefaults = useMemo(() => sanitizeFormState(state), [sanitizeFormState, state])
  const apartmentCharSchema = createCharSchema(sanitizedApKeys)
  const buildingCharSchema = createCharSchema(sanitizedBuKeys)
  const schema = yup.object({
    floorMin: yup.number().min(0).max(50).nullable().optional(),
    floorMax: yup
      .number()
      .min(0)
      .max(50)
      .nullable()
      .optional()
      .test(
        "is-greater-than-floorMin",
        "FloorMax must be greater than or equal to FloorMin",
        function (value) {
          return !value || value >= this.parent.floorMin
        },
      ),
    areaMin: yup.number().min(0).nullable().optional(),
    areaMax: yup
      .number()
      .min(0)
      .nullable()
      .optional()
      .test(
        "is-greater-than-areaMin",
        "AreaMax must be greater than or equal to AreaMin",
        function (value) {
          return !value || value >= this.parent.areaMin
        },
      ),
    date: yup.date().optional(),
    roomNumber: yup.string().nullable().oneOf(roomArray).optional(),
    apartmentChar: yup.object(apartmentCharSchema),
    buildingChar: yup.object(buildingCharSchema),
    buildingNames: yup.array().of(yup.string().required()).optional(),
  })
  type FormInput = yup.InferType<typeof schema>
  const t = useTranslations("FiltersModal")
  floorArr[0] = { value: 0, label: t("basement") }

  // Initialize floor range based on buildingFloorRanges or use static array
  const getInitialFloorRange = useCallback(() => {
    if (buildingFloorRanges && Object.keys(buildingFloorRanges).length > 0) {
      const allRanges = Object.values(buildingFloorRanges)
      const maxFloor = Math.max(...allRanges.map((r) => r.max))
      const minFloor = Math.min(...allRanges.map((r) => r.min))
      const newFloors: { value: number; label: string }[] = []
      for (let f = minFloor; f <= maxFloor; f++) {
        newFloors.push({ value: f, label: f === 0 ? t("basement") : String(f) })
      }
      return newFloors
    }
    return floorArr
  }, [buildingFloorRanges, t])

  const initialFloorRange = useMemo(() => getInitialFloorRange(), [getInitialFloorRange])
  const [floorMax, setFloorMax] = useState(initialFloorRange)
  const [floorMin, setFloorMin] = useState(initialFloorRange)
  const [areaMin, setAreaMin] = useState<number | null>(null)
  const [areaMax, setAreaMax] = useState<number | null>(null)
  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors, touchedFields },
    getValues,
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: formDefaults,
  })

  useEffect(() => {
    if (open) {
      reset(formDefaults)
    }
  }, [formDefaults, open, reset])

  // Update floor ranges based on selected buildings
  const updateFloorRanges = useCallback(
    (selectedBuildings: string[]) => {
      if (buildingFloorRanges && selectedBuildings.length > 0) {
        const ranges = selectedBuildings
          .map((b) => buildingFloorRanges[b])
          .filter((r): r is { min: number; max: number } => !!r)
        if (ranges.length > 0) {
          const min = Math.min(...ranges.map((r) => r.min))
          const max = Math.max(...ranges.map((r) => r.max))
          const newFloors: { value: number; label: string }[] = []
          for (let f = min; f <= max; f++) {
            newFloors.push({ value: f, label: f === 0 ? t("basement") : String(f) })
          }
          setFloorMin(newFloors)
          setFloorMax(newFloors)
          return { min, max }
        }
      }

      const initialRange = getInitialFloorRange()
      setFloorMin(initialRange)
      setFloorMax(initialRange)
      return null
    },
    [buildingFloorRanges, t, getInitialFloorRange],
  )

  useEffect(() => {
    if (open) {
      const selectedBuildings =
        state.buildingNames && Array.isArray(state.buildingNames) ? state.buildingNames : []
      updateFloorRanges(selectedBuildings)
    }
  }, [open, state.buildingNames, updateFloorRanges])

  const countNonObjectFields = (obj: any) => {
    let count = 0
    for (const key in obj) {
      const value = obj[key]
      const isEmptyValue =
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "") ||
        (typeof value === "number" && Number.isNaN(value))

      if (key === "date") {
        if (!isEmptyValue) count++
      } else if (key === "buildingNames" && Array.isArray(value)) {
        const activeBuildings = value.filter((name) => name && name.trim() !== "")
        count += activeBuildings.length
      } else if (
        Object.prototype.hasOwnProperty.call(obj, key) &&
        key !== "apartmentChar" &&
        key !== "buildingChar" &&
        key !== "buildingNames" &&
        key !== "date" &&
        !isEmptyValue
      ) {
        count++
      }
    }
    return count
  }
  const getFiltersCount = () => {
    let totalLength = 0
    const values = getValues()
    const trueValuesCount = Object.values(values.apartmentChar).filter(
      (value) => value === true,
    ).length
    const buildingChar = Object.values(values.buildingChar).filter((value) => value === true).length

    const count = countNonObjectFields(values)
    totalLength = trueValuesCount + buildingChar + count
    setFiltersCount(totalLength)
  }

  // Check if any filters are active (from current form state)
  const hasActiveFilters = () => {
    const values = getValues()

    // Check numeric ranges
    if (values.floorMin !== null || values.floorMax !== null) return true
    if (values.areaMin !== null || values.areaMax !== null) return true

    // Check other fields
    if (values.roomNumber) return true
    if (values.date) return true

    // Check apartment characteristics
    const hasApChars = Object.values(values.apartmentChar).some(Boolean)
    if (hasApChars) return true

    // Check building characteristics
    const hasBuChars = Object.values(values.buildingChar).some(Boolean)
    if (hasBuChars) return true

    // Check building names
    if (values.buildingNames && values.buildingNames.length > 0) return true

    return false
  }

  const onSubmit: SubmitHandler<FormInput> = (data) => {
    jobFilter("Perform filtering", "Perform filtering", "פילטר מתקדם פרויקטים בשיווק ")

    const updatedData: ApState = {
      ...data,
      apartmentChar: desanitizeFeatureValues(
        data.apartmentChar as Record<string, boolean>,
        apKeyMap,
      ),
      buildingChar: desanitizeFeatureValues(data.buildingChar as Record<string, boolean>, buKeyMap),
    }

    getFiltersCount()

    // Update state directly
    setState(updatedData as ApState)

    handleClose()
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset(formDefaults)
        handleClose()
      }}
      aria-labelledby={dialogTitleId}
    >
      <StyledModal onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>
          <NewText
            id={dialogTitleId}
            component={"h2"}
            desktopFontSize={32}
            desktopLineHeight={"36px"}
            mobileFontSize={24}
            mobileLineHeight={"28px"}
            color={sharedColors.white}
          >
            {t("sortBy")}
          </NewText>
          <StyledButton
            role="button"
            tabIndex={0}
            aria-label={t("closeDialog")}
            onClick={handleClose}
          >
            <CircleXIcon />
          </StyledButton>
        </ModalHeader>
        <ModalContent>
          <FirstPart>
            <BodyHeader role="group" aria-labelledby="building-filter-heading">
              {availableBuildings.length > 0 && (
                <>
                  <NewText
                    id="building-filter-heading"
                    component={"h3"}
                    desktopFontSize={16}
                    desktopLineHeight={"20px"}
                    mobileFontSize={16}
                    mobileLineHeight={"17px"}
                    desktopFontWeight={600}
                    color={sharedColors.black}
                  >
                    {t("building")}
                  </NewText>
                  <StyledRange>
                    <Controller
                      name="buildingNames"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel
                            variant="filled"
                            id="select-buildings-label"
                            htmlFor="select-buildings"
                          >
                            {!field.value || (field.value as string[]).length === 0
                              ? t("allBuildings")
                              : t("building")}
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="select-buildings-label"
                            variant="filled"
                            multiple
                            fullWidth
                            value={field.value || []}
                            MenuProps={{
                              disableScrollLock: true,
                              autoFocus: false,
                            }}
                            displayEmpty
                            SelectDisplayProps={{ style: { fontSize: 16 } }}
                            inputProps={{
                              id: "select-buildings",
                              style: { fontSize: 16 },
                            }}
                            onChange={(e) => {
                              const { value } = e.target
                              const selected = typeof value === "string" ? value.split(",") : value
                              setValue("buildingNames", selected)
                              jobFilter(
                                Array.isArray(selected) ? selected.join(", ") : String(selected),
                                "בניין",
                                "פילטר מתקדם פרויקטים בשיווק ",
                              )
                              // Update floor ranges and clamp existing selections if out of new range
                              const range = updateFloorRanges(selected)
                              if (range) {
                                const currentMin = getValues("floorMin")
                                const currentMax = getValues("floorMax")
                                if (
                                  typeof currentMin === "number" &&
                                  (currentMin < range.min || currentMin > range.max)
                                ) {
                                  setValue("floorMin", null)
                                }
                                if (
                                  typeof currentMax === "number" &&
                                  (currentMax < range.min || currentMax > range.max)
                                ) {
                                  setValue("floorMax", null)
                                }
                              }
                            }}
                            renderValue={(selected) => {
                              if (!selected || (selected as string[]).length === 0) {
                                return null
                              }
                              return (selected as string[])
                                .map((b) => `${t("building")} ${b}`)
                                .join(", ")
                            }}
                          >
                            {availableBuildings.map((building) => (
                              <MenuItem key={building} value={building}>
                                <Checkbox
                                  checked={
                                    field.value
                                      ? (field.value as string[]).indexOf(building) > -1
                                      : false
                                  }
                                  inputProps={{ "aria-label": `${t("building")} ${building}` }}
                                />
                                {building}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                    <Dash
                      style={{
                        visibility: "hidden",
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                      }}
                    />
                  </StyledRange>
                </>
              )}
            </BodyHeader>
            <BodyHeader role="group" aria-labelledby="floor-filter-heading">
              <NewText
                id="floor-filter-heading"
                component={"h3"}
                desktopFontSize={16}
                desktopLineHeight={"20px"}
                mobileFontSize={16}
                mobileLineHeight={"17px"}
                desktopFontWeight={600}
                color={sharedColors.black}
              >
                {t("floor")}
              </NewText>
              <StyledRange>
                <Controller
                  name="floorMin"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        variant="filled"
                        id="select-floor-min-label"
                        htmlFor="select-floor-min"
                      >
                        {t("starting_at")}
                      </InputLabel>
                      <Select
                        {...field}
                        value={field.value ?? ""}
                        label={"floorMin"}
                        labelId="select-floor-min-label"
                        variant="filled"
                        error={!!errors.floorMin}
                        fullWidth
                        // value={field.label || ""}

                        MenuProps={{
                          disableScrollLock: true,
                        }}
                        displayEmpty
                        SelectDisplayProps={{ style: { fontSize: 16 } }}
                        inputProps={{
                          id: "select-floor-min",
                          style: { fontSize: 16 },
                        }}
                        onChange={(e) => {
                          const selectedMin = Number(e.target.value)
                          setValue("floorMin", selectedMin)
                          // Filter based on current dynamic range (building specific), not the static floorArr
                          setFloorMax((current) =>
                            current.filter((floor) => floor.value >= selectedMin),
                          )
                        }}
                        onClick={() => jobFilter("החל מ-", "קומה", "פילטר מתקדם פרויקטים בשיווק ")}
                      >
                        {floorMin.map((el) => (
                          <MenuItem style={{ zIndex: 1000000 }} key={el.value} value={el.value}>
                            {el.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText sx={{ color: "#d32f2f" }}>
                        {errors.floorMin?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />

                <Dash />

                <Controller
                  name="floorMax"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        variant="filled"
                        id="select-floor-max-label"
                        htmlFor="select-floor-max"
                      >
                        {t("up_to")}
                      </InputLabel>
                      <Select
                        {...field}
                        value={field.value ?? ""}
                        label={"floorMax"}
                        labelId="select-floor-max-label"
                        variant="filled"
                        error={!!errors.floorMin}
                        fullWidth
                        // value={field.value || ""}
                        MenuProps={{
                          disableScrollLock: true,
                        }}
                        onChange={(e) => {
                          const selectedMax = Number(e.target.value)
                          setValue("floorMax", selectedMax)
                          // Filter based on current dynamic range (building specific), not the static floorArr
                          setFloorMin((current) =>
                            current.filter(
                              (floor) => floor.value <= selectedMax || floor.value === 0,
                            ),
                          )
                        }}
                        displayEmpty
                        SelectDisplayProps={{ style: { fontSize: 16 } }}
                        inputProps={{
                          id: "select-floor-max",
                          style: { fontSize: 16 },
                        }}
                        onClick={() => jobFilter("עד", "קומה", "פילטר מתקדם פרויקטים בשיווק ")}
                      >
                        {floorMax.map((el) => (
                          <MenuItem style={{ zIndex: 1000000 }} key={el.value} value={el.value}>
                            {el.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText sx={{ color: "#d32f2f" }}>
                        {errors.floorMax?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </StyledRange>
            </BodyHeader>
            {!shouldHideFilters && (
              <BodyHeader role="group" aria-labelledby="area-filter-heading">
                <NewText
                  id="area-filter-heading"
                  component={"h3"}
                  desktopFontSize={16}
                  desktopLineHeight={"20px"}
                  mobileFontSize={16}
                  mobileLineHeight={"17px"}
                  desktopFontWeight={600}
                  color={sharedColors.black}
                >
                  {t("area")}
                </NewText>
                <StyledRange>
                  <Controller
                    name="areaMin"
                    control={control}
                    render={({ field }) => (
                      <StyledNumberField
                        {...field}
                        label={t("starting_at")}
                        variant="filled"
                        error={!!errors.areaMin}
                        value={field.value || ""}
                        helperText={errors.areaMin?.message}
                        onBlur={(e) => {
                          const raw = e.target.value
                          if (raw === "" || raw === null) {
                            setValue("areaMin", null)
                            setAreaMin(null)
                            return
                          }
                          const numeric = Number(raw)
                          if (areaMax !== null && numeric > areaMax) {
                            setValue("areaMin", areaMax)
                            setAreaMin(areaMax)
                          } else {
                            setValue("areaMin", numeric)
                            setAreaMin(numeric)
                          }
                        }}
                        onClick={() =>
                          jobFilter("החל מ-", "שטח (במ״ר)", "פילטר מתקדם פרויקטים בשיווק ")
                        }
                      />
                    )}
                  />
                  <Dash />
                  <Controller
                    name="areaMax"
                    control={control}
                    render={({ field }) => (
                      <StyledNumberField
                        {...field}
                        label={t("up_to")}
                        InputProps={{
                          inputProps: {
                            max: 100,
                            min: 10,
                          },
                        }}
                        variant="filled"
                        value={field.value || ""}
                        error={!!errors.areaMax}
                        helperText={errors.areaMax?.message}
                        onBlur={(e) => {
                          const raw = e.target.value
                          if (raw === "" || raw === null) {
                            setValue("areaMax", null)
                            setAreaMax(null)
                            return
                          }
                          const numeric = Number(raw)
                          if (areaMin !== null && numeric < areaMin) {
                            setValue("areaMax", areaMin)
                            setAreaMax(areaMin)
                          } else {
                            setValue("areaMax", numeric)
                            setAreaMax(numeric)
                          }
                        }}
                        onClick={() =>
                          jobFilter("עד", "שטח (במ״ר)", "פילטר מתקדם פרויקטים בשיווק ")
                        }
                      />
                    )}
                  />
                </StyledRange>
              </BodyHeader>
            )}
          </FirstPart>
          <SecondPart>
            {!shouldHideFilters && (
              <Width>
                <BodyHeader role="group" aria-labelledby="date-filter-heading">
                  <NewText
                    id="date-filter-heading"
                    component={"h3"}
                    desktopFontSize={16}
                    desktopLineHeight={"20px"}
                    mobileFontSize={16}
                    mobileLineHeight={"17px"}
                    desktopFontWeight={600}
                    color={sharedColors.black}
                  >
                    {t("available_date")}
                  </NewText>
                  <DateStyled>
                    <Controller
                      control={control}
                      name="date"
                      render={({ field }) => {
                        return (
                          <LocalizationProvider adapterLocale={locale} dateAdapter={AdapterDayjs}>
                            <StyledDatePicker
                              sx={{
                                ".MuiInputBase-root": { alignItems: "center" },
                                ".MuiOutlinedInput-root": {
                                  fieldset: {
                                    borderColor: "black",
                                  },
                                },
                              }}
                              slotProps={{
                                inputAdornment: { position: "start" },
                                textField: {
                                  placeholder: "החל מ - ",
                                  inputProps: { "aria-label": t("available_date") },
                                },
                              }}
                              slots={{ openPickerIcon: CalendarIcon }}
                              value={field.value ? dayjs(field.value) : null}
                              inputRef={field.ref}
                              onChange={(date) => {
                                field.onChange(date)
                                jobFilter("החל מ-", "תאריך כניסה", "פילטר מתקדם פרויקטים בשיווק ")
                              }}
                            />
                          </LocalizationProvider>
                        )
                      }}
                    />
                  </DateStyled>
                </BodyHeader>
              </Width>
            )}
            <Width>
              <Numbers>
                <BodyHeader role="group" aria-labelledby="rooms-filter-heading">
                  <NewText
                    id="rooms-filter-heading"
                    component={"h3"}
                    desktopFontSize={16}
                    desktopLineHeight={"20px"}
                    mobileFontSize={16}
                    mobileLineHeight={"17px"}
                    desktopFontWeight={600}
                    color={sharedColors.black}
                  >
                    {t("numberOfRooms")}
                  </NewText>
                  <Controller
                    name="roomNumber"
                    control={control}
                    render={({ field }) => (
                      <StyledToggleButtonGroup
                        {...field}
                        exclusive
                        aria-label={t("numberOfRooms")}
                        onChange={(event: React.MouseEvent<HTMLElement>, value: string) => {
                          setValue(field.name, value)
                          jobFilter(value, "מספר חדרים", "פילטר מתקדם פרויקטים בשיווק ")
                        }}
                      >
                        {roomArray.map((el) => (
                          <StyledToggleButton value={el} key={el}>
                            {el}
                          </StyledToggleButton>
                        ))}
                      </StyledToggleButtonGroup>
                    )}
                  />
                </BodyHeader>
              </Numbers>
            </Width>
          </SecondPart>
          {!shouldHideFilters && (
            <ThirdPart>
              <BodyHeader role="group" aria-labelledby="apt-features-heading">
                <NewText
                  id="apt-features-heading"
                  component={"h3"}
                  desktopFontSize={16}
                  desktopLineHeight={"20px"}
                  mobileFontSize={16}
                  mobileLineHeight={"17px"}
                  desktopFontWeight={600}
                  color={sharedColors.black}
                >
                  {t("aptFeatures")}
                </NewText>
                <CheckArray>
                  {allApChar.map((el, index) => {
                    const labelId = `apt-feature-${index}-label`
                    return (
                      <Controller
                        key={index}
                        control={control}
                        name={`apartmentChar.${sanitizeFeatureKey(el)}`}
                        render={({ field }) => {
                          return (
                            <StyledCheckFlex>
                              <StyledCheckbox
                                error={false}
                                {...field}
                                icon={<CheckboxIcon />}
                                checkedIcon={<CheckedCheckboxIcon />}
                                checked={field.value}
                                inputProps={{ "aria-label": el }}
                                sx={{
                                  ":hover": {
                                    backgroundColor: "transparent",
                                  },
                                }}
                                onChange={(e) => {
                                  jobFilter(el, "מאפייני דירה", "פילטר מתקדם פרויקטים בשיווק "),
                                    field.onChange(e.target.checked),
                                    setValue(
                                      `apartmentChar.${sanitizeFeatureKey(el)}`,
                                      e.target.checked,
                                      {
                                        shouldTouch: true,
                                      },
                                    )
                                }}
                              />
                              <NewText
                                id={labelId}
                                component={"span"}
                                desktopFontSize={16}
                                desktopLineHeight={"20px"}
                                mobileFontSize={16}
                                mobileLineHeight={"17px"}
                                color={sharedColors.black}
                              >
                                {el}
                              </NewText>{" "}
                            </StyledCheckFlex>
                          )
                        }}
                      />
                    )
                  })}
                </CheckArray>
              </BodyHeader>
              {projectPage ? null : (
                <BodyHeader role="group" aria-labelledby="building-features-heading">
                  <NewText
                    id="building-features-heading"
                    component={"h3"}
                    desktopFontSize={16}
                    desktopLineHeight={"20px"}
                    mobileFontSize={16}
                    mobileLineHeight={"17px"}
                    desktopFontWeight={600}
                    color={sharedColors.black}
                  >
                    {t("building_features")}
                  </NewText>
                  <CheckArray>
                    {allBuChar.map((el, index) => {
                      const labelId = `building-feature-${index}-label`
                      return (
                        <Controller
                          key={index}
                          control={control}
                          name={`buildingChar.${sanitizeFeatureKey(el)}`}
                          render={({ field }) => {
                            const isSelected =
                              field.value !== undefined
                                ? field.value
                                : getValues(`buildingChar.${sanitizeFeatureKey(el)}`)
                            return (
                              <StyledCheckFlex>
                                <StyledCheckbox
                                  {...field}
                                  error={false}
                                  icon={<CheckboxIcon />}
                                  checked={isSelected}
                                  checkedIcon={<CheckedCheckboxIcon />}
                                  inputProps={{ "aria-label": el }}
                                  onChange={(e) => {
                                    jobFilter(el, "מאפייני בניין", "פילטר מתקדם פרויקטים בשיווק ")

                                    field.onChange(e.target.checked)
                                    setValue(
                                      `buildingChar.${sanitizeFeatureKey(el)}`,
                                      e.target.checked,
                                      {
                                        shouldTouch: true,
                                      },
                                    )
                                  }}
                                  sx={{
                                    ":hover": {
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                />
                                <NewText
                                  id={labelId}
                                  component={"span"}
                                  desktopFontSize={16}
                                  desktopLineHeight={"20px"}
                                  mobileFontSize={16}
                                  mobileLineHeight={"17px"}
                                  color={sharedColors.black}
                                >
                                  {el}
                                </NewText>
                              </StyledCheckFlex>
                            )
                          }}
                        />
                      )
                    })}
                  </CheckArray>
                </BodyHeader>
              )}
            </ThirdPart>
          )}
        </ModalContent>
        <ModalFooter>
          <div>
            {Object.keys(touchedFields).length > 0 ||
              (hasActiveFilters() && (
                <StyledResetButton
                  onClick={(e) => {
                    e.preventDefault()
                    jobFilter("איפוס", "clear all button", "פילטר מתקדם פרויקטים בשיווק ")
                    reset(sanitizeFormState(defaultState))
                    handleReset()
                  }}
                >
                  {locale === "he" ? "איפוס" : "clear"}
                </StyledResetButton>
              ))}
          </div>
          <Button variant="outlined" size="large" type="submit">
            {t("apply")}
          </Button>
        </ModalFooter>
      </StyledModal>
    </Modal>
  )
}

export default FiltersModal
